/**
 * V2-dark create-project form — a re-skin of CreateProjectModal's create flow
 * (web/src/components/layout/ProjectContextHeader.tsx). Prefilled from the
 * estimate header; submits via the project store's `createProject`, the same
 * path V1 uses. Minimal field set (address + owner + claim) with a Google
 * Places quick-address lookup; policy/loss/claim-type fields are a follow-up.
 */
import { useState } from 'react';
import type { ChangeEvent, ReactNode, SyntheticEvent } from 'react';

import useProjectStore from '../../stores/projectStore';
import type { NewProjectForm, Project, StructuredAddress } from '../../types';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { DAMAGE_TYPES } from '../../data/roomChecklists';
import { IconCheck } from './icons';

/** Initial values accepted by the form (superset of NewProjectForm). */
export type CreateProjectInitialValues = Partial<
  NewProjectForm & { policy_number?: string; loss_type?: string; claim_type?: string }
>;

/** Props for the V2 CreateProjectForm. */
export interface CreateProjectFormProps {
  initialValues?: CreateProjectInitialValues;
  onClose: () => void;
  onCreated: (_project: Project) => void;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
}

interface FormState extends NewProjectForm {
  policy_number?: string;
  loss_type?: string;
  claim_type?: string;
}

/** A labelled field wrapper matching V2 form density. */
function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block space-y-1">
      <span className="block text-[11px] font-[550] text-[var(--tx-lo)]">{label}</span>
      {children}
    </label>
  );
}

/** V2-dark create-project form, rendered inside a Modal. */
export default function CreateProjectForm({
  initialValues,
  onClose,
  onCreated,
  title = 'Create project from estimate',
  subtitle = 'Prefilled from the estimate header. Edit anything before saving.',
  submitLabel = 'Create and use',
}: CreateProjectFormProps) {
  const { createProject } = useProjectStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    street: '',
    city: '',
    state: '',
    zip: '',
    owner_name: '',
    claim_number: '',
    policy_number: '',
    loss_type: '',
    claim_type: '',
    // Required at creation, immutable after (PRP-ROOF-2 S0). Residential preselected.
    project_type: 'residential',
    ...initialValues,
  });

  const set = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  // Reason: Auto-fill street/city/state/zip from a Places selection; empty
  // fields (e.g. the no-key fallback) keep existing values rather than clobber.
  const handleAddressSelect = (addr: StructuredAddress) =>
    setForm((p) => ({
      ...p,
      street: addr.street || p.street,
      city: addr.city || p.city,
      state: addr.state || p.state,
      zip: addr.zip || p.zip,
    }));

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!form.street.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim()) {
      setError('Street, city, state, and ZIP are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    // Match V1: send only the populated core fields to avoid backend noise.
    const payload: NewProjectForm = {
      street: form.street.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zip: form.zip.trim(),
    };
    if (form.owner_name?.trim()) payload.owner_name = form.owner_name.trim();
    if (form.claim_number?.trim()) payload.claim_number = form.claim_number.trim();
    if (form.loss_type?.trim()) payload.loss_type = form.loss_type.trim();
    // Always sent — required at creation, immutable after (PRP-ROOF-2 S0).
    payload.project_type = form.project_type ?? 'residential';
    try {
      const created = await createProject(payload);
      if (!created) {
        setError('Failed to create project.');
        return;
      }
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      label="Create new project"
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      testId="create-project-form"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {/* type="button" + onClick (not native submit) keeps this robust in
              jsdom where the button sits outside the <form> subtree. */}
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting}>
            <IconCheck />
            {submitting ? 'Creating…' : submitLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && <div className="text-[12px] text-[var(--bad)]">{error}</div>}
        <label className="block space-y-1">
          <span className="block text-[11px] font-[550] text-[var(--tx-lo)]">Quick address lookup</span>
          <AddressAutocomplete
            onSelect={handleAddressSelect}
            placeholder="Start typing an address to auto-fill…"
            className="h-[34px] rounded-[var(--r-md)] bg-[var(--bg-inset)] border-[var(--bd)] text-[length:13px] text-[color:var(--tx-hi)] placeholder:text-[color:var(--tx-faint)] focus:ring-0 focus:border-[var(--accent-line)] focus:shadow-[0_0_0_3px_var(--accent-soft)]"
          />
        </label>
        <Field label="Street address *" htmlFor="v2-street">
          <Input
            id="v2-street"
            value={form.street}
            onChange={set('street')}
            required
            autoComplete="address-line1"
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="City *" htmlFor="v2-city">
            <Input id="v2-city" value={form.city} onChange={set('city')} required />
          </Field>
          <Field label="State *" htmlFor="v2-state">
            <Input id="v2-state" value={form.state} onChange={set('state')} required />
          </Field>
          <Field label="ZIP *" htmlFor="v2-zip">
            <Input id="v2-zip" value={form.zip} onChange={set('zip')} required />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Owner name" htmlFor="v2-owner">
            <Input id="v2-owner" value={form.owner_name ?? ''} onChange={set('owner_name')} />
          </Field>
          <Field label="Claim number" htmlFor="v2-claim">
            <Input id="v2-claim" value={form.claim_number ?? ''} onChange={set('claim_number')} />
          </Field>
        </div>
        <Field label="Loss type" htmlFor="v2-loss-type">
          <Select
            id="v2-loss-type"
            value={form.loss_type ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, loss_type: e.target.value }))}
          >
            <option value="">Select loss type…</option>
            {/* Header-extracted value outside the canonical list stays selectable. */}
            {form.loss_type &&
              !DAMAGE_TYPES.some((d) => d.value === form.loss_type) && (
                <option value={form.loss_type}>{form.loss_type}</option>
              )}
            {DAMAGE_TYPES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </Field>
        {/* Required at creation, immutable after (PRP-ROOF-2 S0). Residential preselected. */}
        <Field label="Property type *" htmlFor="v2-project-type">
          <Select
            id="v2-project-type"
            data-testid="create-project-type"
            value={form.project_type ?? 'residential'}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                project_type: e.target.value as 'residential' | 'commercial',
              }))
            }
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </Select>
        </Field>
      </form>
    </Modal>
  );
}
